import { useEffect, useState } from "react";
import { useSessionStore } from "../state/useSessionStore";
import { formatCountdown, formatRestSeconds, formatKg } from "../lib/format";
import { EXERCISE_BY_ID } from "../data/exercises";
import { calculateBarbellPlates, calculateDumbbellWeight } from "@jilian/core";

/**
 * 休息计时横幅（阶段 3b）。
 * - 绝对时间：只依赖 startAt / targetEndAt / durationSeconds，恢复前台按当前时间重算。
 * - Display 级别倒计时数字（40–48px tabular-nums）+ 2px 极细进度条。
 * - 组数推进指示：极简圆点进度（●●○○）。
 * - 下一动作/下一组配重预渲染：休息期间提前展示单侧杠铃片组合或哑铃档位，方便提前装片。
 * - 按钮点击区域 ≥48×48px。
 */
export function RestTimer() {
  const rest = useSessionStore((s) => s.rest);
  const extendRest = useSessionStore((s) => s.extendRest);
  const cancelRest = useSessionStore((s) => s.cancelRest);
  const exercises = useSessionStore((s) => s.exercises);
  const [now, setNow] = useState(() => Date.now());

  const active = rest != null;

  useEffect(() => {
    if (!active) return;
    const tick = () => setNow(Date.now());
    const id = window.setInterval(tick, 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [active]);

  if (!rest) return null;

  const remainingMs = rest.targetEndAt - now;
  const remaining = Math.max(0, Math.ceil(remainingMs / 1000));
  const finished = remainingMs <= 0;
  const progressPct = Math.min(100, Math.max(0, ((rest.durationSeconds * 1000 - remainingMs) / (rest.durationSeconds * 1000)) * 100));

  // 计算当前动作与下一动作/组的配重方案
  const currentExIndex = exercises.findIndex((e) => e.exerciseId === rest.exerciseId);
  const currentEx = exercises[currentExIndex];

  let nextLabel = "";
  let plateHint = "";

  if (currentEx) {
    const nextSet = currentEx.sets.find((s) => s.setIndex === rest.setIndex + 1);
    if (nextSet) {
      const weight = nextSet.weightKg ?? currentEx.suggestedWeightKg ?? 0;
      nextLabel = `下一组：第 ${nextSet.setIndex} 组 · ${formatKg(weight)} kg`;
      if (currentEx.weightUnitScope === "total") {
        const plates = calculateBarbellPlates({ targetWeightKg: weight });
        plateHint = `单侧挂片：${plates.platesPerSideFormatted}`;
      } else if (currentEx.weightUnitScope === "per_dumbbell") {
        const dbRes = calculateDumbbellWeight(weight);
        plateHint = `每只哑铃：${dbRes.suggestedWeightKg} kg`;
      }
    } else if (currentExIndex + 1 < exercises.length) {
      const nextEx = exercises[currentExIndex + 1]!;
      const nextExDef = EXERCISE_BY_ID.get(nextEx.exerciseId);
      const nextWeight = nextEx.suggestedWeightKg ?? 0;
      nextLabel = `下一动作：${nextExDef?.nameZh ?? "下一项"} · ${formatKg(nextWeight)} kg`;
      if (nextEx.weightUnitScope === "total") {
        const plates = calculateBarbellPlates({ targetWeightKg: nextWeight });
        plateHint = `单侧挂片：${plates.platesPerSideFormatted}`;
      } else if (nextEx.weightUnitScope === "per_dumbbell") {
        const dbRes = calculateDumbbellWeight(nextWeight);
        plateHint = `每只哑铃：${dbRes.suggestedWeightKg} kg`;
      }
    } else {
      nextLabel = "本日训练即将圆满完成！";
    }
  }

  return (
    <div
      className={`fixed left-1/2 -translate-x-1/2 z-40 w-[calc(100%-24px)] max-w-[460px] rounded-card border shadow-xl backdrop-blur-xl transition-all duration-200 overflow-hidden ${
        finished
          ? "bg-tone-card border-tone-good/60 shadow-tone-good/10"
          : "bg-tone-card border-tone-accent shadow-tone-accent/15 text-tone-ink"
      }`}
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 76px)" }}
      role="status"
      aria-live="polite"
    >
      {/* 2px 极细横向进度条 */}
      <div className="h-[2.5px] w-full bg-black/5 dark:bg-white/10 overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${finished ? "bg-tone-good" : "bg-tone-accent"}`}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <div className="p-3.5 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div>
            {/* 动作状态与圆点进度 */}
            <div className="flex items-center gap-2">
              <span className={`text-[12px] font-semibold font-caption ${finished ? "text-tone-good" : "text-tone-soft"}`}>
                {finished ? "休息已完成" : `组间休息（设定 ${formatRestSeconds(rest.durationSeconds)}）`}
              </span>
              {currentEx && (
                <div className="flex items-center gap-1">
                  {currentEx.sets.map((s) => (
                    <span
                      key={s.setIndex}
                      className={`w-1.5 h-1.5 rounded-full ${
                        s.completed ? "bg-tone-good" : "bg-black/20 dark:bg-white/30"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* 40-48px Display 级别大倒计时数字 */}
            <div className={`text-[36px] sm:text-[40px] font-extrabold tabular-nums leading-none mt-1 tracking-tight font-display ${
              finished ? "text-tone-good animate-bounce" : "text-tone-ink"
            }`}>
              {finished ? "准备开始！" : formatCountdown(remaining)}
            </div>
          </div>

          {/* 控制按钮（≥48×48px 触控区） */}
          <div className="flex items-center gap-2">
            {!finished ? (
              <>
                <button
                  onClick={() => extendRest(30)}
                  className="min-h-[48px] px-3.5 rounded-elem bg-tone-accentSoft text-tone-accent font-bold text-[14px] active:scale-95 transition-all"
                  aria-label="延长30秒"
                >
                  +30s
                </button>
                <button
                  onClick={cancelRest}
                  className="min-h-[48px] px-3.5 rounded-elem bg-black/5 dark:bg-white/10 text-tone-ink font-semibold text-[14px] active:scale-95 transition-all"
                >
                  跳过
                </button>
              </>
            ) : (
              <button
                onClick={cancelRest}
                className="min-h-[48px] px-5 rounded-elem bg-tone-good text-white font-bold text-[15px] shadow-sm active:scale-95 transition-all"
              >
                我准备好了
              </button>
            )}
          </div>
        </div>

        {/* 休息期间配重预渲染提示 */}
        {nextLabel && (
          <div className="pt-2 border-t border-tone-border flex items-center justify-between text-[12px] font-medium font-caption">
            <span className="text-tone-ink font-semibold truncate max-w-[65%]">{nextLabel}</span>
            {plateHint && (
              <span className="text-tone-accent font-bold bg-tone-accentSoft/80 px-2 py-0.5 rounded-elem">
                {plateHint}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
